<script setup lang="ts">
import { t } from '@/i18n';
import { en } from '@/i18n/en';

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};

const faqItems = computed(() => [
  {
    question: t('pricing.page.faq.items.whatIsVeesoAI.question'),
    answerParagraph1: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph2'),
    answerParagraph3: t('pricing.page.faq.items.whatIsVeesoAI.answerParagraph3'),
    products: (() => {
      const products = t('pricing.page.faq.items.whatIsVeesoAI.products');
      return Array.isArray(products) ? products : [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.whatIsCredit.question'),
    answerParagraph1: t('pricing.page.faq.items.whatIsCredit.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.whatIsCredit.answerParagraph2'),
    answerParagraph3: t('pricing.page.faq.items.whatIsCredit.answerParagraph3'),
  },
  {
    question: t('pricing.page.faq.items.getMoreCredits.question'),
    answerParagraph1: t('pricing.page.faq.items.getMoreCredits.answerParagraph1'),
    listItems: [
      t('pricing.page.faq.items.getMoreCredits.answerParagraph2'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph3'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph4'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph5'),
      t('pricing.page.faq.items.getMoreCredits.answerParagraph6'),
    ],
  },
  {
    question: t('pricing.page.faq.items.creditExpiry.question'),
    answerParagraph1: t('pricing.page.faq.items.creditExpiry.answerParagraph1'),
    listItems: (() => {
      const listItemsData = en.pricing?.page?.faq?.items?.creditExpiry?.listItems;
      if (listItemsData && typeof listItemsData === 'object') {
        return [
          listItemsData.dailyCredits,
          listItemsData.newUserBonus,
          listItemsData.referralCredits,
          listItemsData.promotionCredits,
          listItemsData.monthlySubscription,
          listItemsData.yearlySubscription,
        ].filter(Boolean);
      }
      return [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.creditConsumption.question'),
    answerParagraph1: t('pricing.page.faq.items.creditConsumption.answerParagraph1'),
    listItems: (() => {
      const listItemsData = en.pricing?.page?.faq?.items?.creditConsumption?.listItems;
      if (listItemsData && typeof listItemsData === 'object') {
        return [
          listItemsData.dailyCredits,
          listItemsData.promotionCredits,
          listItemsData.referralCredits,
          listItemsData.newUserBonus,
          listItemsData.subscriptionCredits,
        ].filter(Boolean);
      }
      return [];
    })(),
  },
  {
    question: t('pricing.page.faq.items.inviteFriends.question'),
    answerParagraph1: t('pricing.page.faq.items.inviteFriends.answerParagraph1'),
    link: (() => {
      const linkData = en.pricing?.page?.faq?.items?.inviteFriends?.link;
      return linkData || null;
    })(),
    answerParagraph2: t('pricing.page.faq.items.inviteFriends.answerParagraph2'),
  },
  {
    question: t('pricing.page.faq.items.designOwnership.question'),
    answer: t('pricing.page.faq.items.designOwnership.answer'),
  },
  {
    question: t('pricing.page.faq.items.commercialUse.question'),
    answerParagraph1: t('pricing.page.faq.items.commercialUse.answerParagraph1'),
    answerParagraph2: t('pricing.page.faq.items.commercialUse.answerParagraph2'),
  },
  {
    question: t('pricing.page.faq.items.studentDiscounts.question'),
    answer: t('pricing.page.faq.items.studentDiscounts.answer'),
  },
  {
    question: t('pricing.page.faq.items.contactTeam.question'),
    answer: t('pricing.page.faq.items.contactTeam.answer'),
    link: (() => {
      const linkData = en.pricing?.page?.faq?.items?.contactTeam?.link;
      return linkData || null;
    })(),
    answerAfterLink: t('pricing.page.faq.items.contactTeam.answerAfterLink'),
  },
  // Temporarily hidden
  // {
  //   question: t('pricing.page.faq.items.creditUsage.question'),
  //   answer: t('pricing.page.faq.items.creditUsage.answer'),
  //   table: (() => {
  //     const tableData = en.pricing?.page?.faq?.items?.creditUsage?.table;
  //     if (!tableData || !tableData.headers || !tableData.rows) {
  //       return { headers: ['Feature', 'Mode', 'Credits per time'], rows: [] };
  //     }
  //     const originalRows = tableData.rows;
  //     const processedRows: Array<Array<string | { text: string; rowspan: number }>> = [];
  //     let currentFeature = '';
  //     let featureStartIndex = -1;
  //     let featureCount = 0;
  //     originalRows.forEach((row: string[]) => {
  //       const feature = row[0] || '';
  //       if (feature !== currentFeature) {
  //         if (currentFeature !== '' && featureStartIndex >= 0 && featureCount > 1) {
  //           const firstRow = processedRows[featureStartIndex];
  //           if (firstRow) firstRow[0] = { text: currentFeature, rowspan: featureCount };
  //         }
  //         currentFeature = feature || '';
  //         featureStartIndex = processedRows.length;
  //         featureCount = 1;
  //         processedRows.push([...row]);
  //       } else {
  //         featureCount++;
  //         processedRows.push([...row.slice(1)]);
  //       }
  //     });
  //     if (currentFeature !== '' && featureStartIndex >= 0 && featureCount > 1) {
  //       const firstRow = processedRows[featureStartIndex];
  //       if (firstRow) firstRow[0] = { text: currentFeature, rowspan: featureCount };
  //     }
  //     return { headers: tableData.headers || [], rows: processedRows };
  //   })(),
  // },
  {
    question: t('pricing.page.faq.items.tieredPricing.question'),
    answer: t('pricing.page.faq.items.tieredPricing.answer'),
  },
  {
    question: t('pricing.page.faq.items.creditRollovers.question'),
    answer: t('pricing.page.faq.items.creditRollovers.answer'),
  },
  {
    question: t('pricing.page.faq.items.upgradingDowngrading.question'),
    answer: t('pricing.page.faq.items.upgradingDowngrading.answer'),
  },
  {
    question: t('pricing.page.faq.items.cancelSubscription.question'),
    answer: t('pricing.page.faq.items.cancelSubscription.answer'),
  },
  {
    question: t('pricing.page.faq.items.getInvoice.question'),
    answer: t('pricing.page.faq.items.getInvoice.answer'),
  },
  {
    question: t('pricing.page.faq.items.planReset.question'),
    answer: t('pricing.page.faq.items.planReset.answer'),
  },
  {
    question: t('pricing.page.faq.items.dailyCreditsReset.question'),
    answer: t('pricing.page.faq.items.dailyCreditsReset.answer'),
  },
]);
</script>

<template>
  <div class="mt-24 flex flex-col md:flex-row max-w-[800px] mx-auto">
    <!-- Left: Title -->
    <div class="md:w-[280px] md:flex-shrink-0 mb-8 md:mb-0">
      <h2 class="text-3xl font-normal text-black/90 md:sticky md:top-24 py-3 px-5 font-semibold">
        {{ t('pricing.page.faq.title') }}
      </h2>
    </div>

    <!-- Right: FAQ Items -->
    <div class="flex-1 md:w-[520px] space-y-2">
      <div
        v-for="(item, index) in faqItems"
        :key="index"
        class="bg-black/[0.02] rounded-2xl px-5 transition-all duration-300"
      >
        <button
          type="button"
          @click="toggleFaq(index)"
          class="flex items-center justify-between w-full py-4 text-left font-normal transition-all text-black/90"
        >
          <h3 class="text-xl pr-4">{{ item.question }}</h3>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
            class="h-5 w-5 shrink-0 text-black/40 transition-transform duration-200"
            :class="[expandedFaqIndex === index ? 'rotate-90' : '']"
          >
            <path
              fill="currentColor"
              d="M9.47 6.47a.75.75 0 0 1 1.06 0l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 1 1-1.06-1.06L13.94 12 9.47 7.53a.75.75 0 0 1 0-1.06"
            />
          </svg>
        </button>
        <div
          v-show="expandedFaqIndex === index"
          class="pb-5 transition-all duration-200 ease-in-out"
        >
          <div v-if="item.answerParagraph1" class="space-y-3">
            <p class="text-base text-black/70 font-normal">
              {{ item.answerParagraph1 }}
              <span v-if="(item as any).link">
                <a
                  :href="(item as any).link.url"
                  :target="(item as any).link.url.startsWith('http') ? '_blank' : '_self'"
                  :rel="(item as any).link.url.startsWith('http') ? 'noopener noreferrer' : ''"
                  class="underline inline-flex items-center ml-1"
                >
                  {{ (item as any).link.text }}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-4 h-4 ml-1"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </span>
            </p>
            <!-- List items -->
            <ul
              v-if="(item as any).listItems && (item as any).listItems.length > 0"
              class="space-y-2.5 pl-0 mt-3"
            >
              <li
                v-for="(listItem, listIndex) in (item as any).listItems"
                :key="listIndex"
                class="text-base text-black/70 font-normal flex items-start"
              >
                <span class="text-black/40 mr-3 flex-shrink-0 mt-0.5">•</span>
                <span>{{ listItem.replace('• ', '') }}</span>
              </li>
            </ul>
            <!-- Regular paragraphs -->
            <template v-else>
              <p v-if="(item as any).answerParagraph2" class="text-base text-black/70 font-normal">
                {{ (item as any).answerParagraph2 }}
              </p>
              <p v-if="(item as any).answerParagraph3" class="text-base text-black/70 font-normal">
                {{ (item as any).answerParagraph3 }}
                <span v-if="(item as any).products && (item as any).products.length > 0">
                  <template
                    v-for="(product, productIndex) in (item as any).products"
                    :key="productIndex"
                  >
                    <template v-if="productIndex === 0">{{ product }}</template>
                    <template v-else-if="productIndex === (item as any).products.length - 1"
                      >, and {{ product }}</template
                    >
                    <template v-else>, {{ product }}</template>
                  </template>
                  <span>.</span>
                </span>
              </p>
              <p v-if="(item as any).answerParagraph4" class="text-base text-black/70 font-normal">
                {{ (item as any).answerParagraph4 }}
              </p>
              <p v-if="(item as any).answerParagraph5" class="text-base text-black/70 font-normal">
                {{ (item as any).answerParagraph5 }}
              </p>
              <p v-if="(item as any).answerParagraph6" class="text-base text-black/70 font-normal">
                {{ (item as any).answerParagraph6 }}
              </p>
            </template>
          </div>
          <p v-else class="text-base text-black/70 font-normal">
            {{ item.answer }}
            <span v-if="(item as any).link">
              <a
                :href="(item as any).link.url"
                target="_blank"
                rel="noopener noreferrer"
                class="underline inline-flex items-center ml-1"
              >
                {{ (item as any).link.text }}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="w-4 h-4 ml-1"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>
            </span>
            <br v-if="(item as any).answerAfterLink" />
            <span v-if="(item as any).answerAfterLink">{{ (item as any).answerAfterLink }}</span>
          </p>
          <div
            v-if="item.table && item.table.headers && item.table.rows"
            class="my-4 overflow-x-auto"
          >
            <table class="w-full border-collapse text-sm text-left">
              <thead>
                <tr class="border-b border-black/10">
                  <th
                    v-for="(header, headerIndex) in item.table.headers"
                    :key="headerIndex"
                    class="text-left py-3 px-4 font-normal text-black/70 align-middle"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in item.table.rows"
                  :key="rowIndex"
                  :class="rowIndex !== item.table.rows.length - 1 ? 'border-b border-black/5' : ''"
                >
                  <template v-for="(cell, cellIndex) in row" :key="cellIndex">
                    <td
                      v-if="cellIndex === 0 && typeof cell === 'object' && cell.rowspan > 0"
                      :rowspan="cell.rowspan"
                      class="py-3 px-4 text-black/60 font-normal align-middle text-left"
                    >
                      {{ cell.text }}
                    </td>
                    <td
                      v-else-if="
                        cellIndex === 0 &&
                        typeof cell === 'string' &&
                        row.length === item.table.headers.length
                      "
                      class="py-3 px-4 text-black/60 font-normal align-middle text-left"
                    >
                      {{ cell }}
                    </td>
                    <td v-else class="py-3 px-4 text-black/60 font-normal align-middle text-left">
                      {{ typeof cell === 'object' ? cell.text : cell }}
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
